import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  fetchAdminCatalogDocuments,
  uploadClassicDocument,
  deleteClassicDocument,
  type ClassicTierStatus,
} from '../../lib/adminApi';
import { AdminPageShell, cls } from './shared';

export function CatalogDocumentsPage() {
  const [priced, setPriced] = useState<ClassicTierStatus | null>(null);
  const [unpriced, setUnpriced] = useState<ClassicTierStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    fetchAdminCatalogDocuments()
      .then((r) => {
        setPriced(r.classic.priced);
        setUnpriced(r.classic.unpriced);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  if (loading) {
    return (
      <AdminPageShell title="Classic Catalog">
        <p className="text-vari-gray text-sm">Loading…</p>
      </AdminPageShell>
    );
  }
  if (error) {
    return (
      <AdminPageShell title="Classic Catalog">
        <p className="text-red-600 text-sm">{error}</p>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell title="Classic Catalog">
      <p className="text-sm text-vari-gray mb-6 max-w-2xl">
        Upload the Classic (original-format) Idea Starter catalog PDFs. Once a
        file is dropped in, it gets a shareable link and PDF download on the home
        screen's <span className="font-medium text-vari-navy">Classic version</span> card —
        the same way the Photorealistic catalog works. Re-uploading replaces the
        file while keeping the existing share link stable.
      </p>

      <div className="grid gap-5 sm:grid-cols-2 max-w-4xl">
        <TierUploader
          label="With pricing"
          pricing={true}
          status={priced}
          onChange={setPriced}
        />
        <TierUploader
          label="Without pricing"
          pricing={false}
          status={unpriced}
          onChange={setUnpriced}
        />
      </div>
    </AdminPageShell>
  );
}

function TierUploader({
  label,
  pricing,
  status,
  onChange,
}: {
  label: string;
  pricing: boolean;
  status: ClassicTierStatus | null;
  onChange: (s: ClassicTierStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const available = status?.available ?? false;

  async function doUpload(file: File) {
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      alert('Please choose a PDF file.');
      return;
    }
    setBusy(true);
    try {
      const next = await uploadClassicDocument(pricing, file);
      onChange(next);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function doRemove() {
    if (!confirm(`Remove the ${label.toLowerCase()} Classic catalog? The home-screen download for this tier will stop working.`)) return;
    setBusy(true);
    try {
      const next = await deleteClassicDocument(pricing);
      onChange(next);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void doUpload(file);
  }

  async function copyLink() {
    if (!status?.url) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${status.url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div className="rounded-lg border border-vari-soft-gray bg-white shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-vari-navy">{label}</h2>
        <span
          className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
            available ? 'bg-vari-green/40 text-vari-navy' : 'bg-vari-soft-gray/40 text-vari-gray'
          }`}
        >
          {available ? 'Published' : 'Not uploaded'}
        </span>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
          dragOver ? 'border-vari-navy bg-vari-beige/40' : 'border-vari-soft-gray hover:border-vari-navy/60 bg-gray-50'
        } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void doUpload(file);
            e.target.value = '';
          }}
        />
        <p className="text-sm text-vari-navy font-medium">
          {busy ? 'Uploading…' : available ? 'Drop a PDF to replace' : 'Drop a PDF here'}
        </p>
        <p className="text-xs text-vari-gray mt-0.5">or click to browse</p>
      </div>

      {/* Current file + actions */}
      {available && status ? (
        <div className="space-y-2.5">
          <div className="text-xs text-vari-gray">
            <p className="truncate">
              <span className="text-vari-navy font-medium">{status.originalName ?? 'catalog.pdf'}</span>
            </p>
            {status.uploadedAt && (
              <p>Uploaded {new Date(status.uploadedAt).toLocaleString()}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={copyLink} className={cls.btnOutline}>
              {copied ? '✓ Link copied' : 'Create share link'}
            </button>
            {status.url && (
              <a href={status.url} target="_blank" rel="noreferrer" className={cls.btnOutline}>
                Open PDF
              </a>
            )}
            <button type="button" onClick={() => void doRemove()} disabled={busy} className={cls.btnDanger}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-vari-gray">
          No file uploaded yet.
          {pricing && ' Falls back to the built-in original catalog until one is added.'}
        </p>
      )}
    </div>
  );
}
